import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import ShareReferralModal from '../ShareReferralModal';

const generatePebbleCode = (userId) => {
  if (!userId) {
    // Generate a random 5-digit number if no userId exists
    return `PEB${String(Math.floor(10000 + Math.random() * 90000)).padStart(5, '0')}`;
  }
  
  // Take the last 8 characters of userId and convert them to a 5-digit number
  const hash = userId.slice(-8).split('').reduce((acc, char) => {
    // Convert character to a number (using char code)
    const num = char.charCodeAt(0);
    return (acc + num) % 100000;
  }, 0);

  // Ensure it's always 5 digits by padding with zeros
  return `PEB${String(hash).padStart(5, '0')}`;
};

const ReferralContent = ({ userDetails }) => {
  const { user } = useAuth();
  const referralCode = userDetails?.referralCode || generatePebbleCode(user?.uid);
  const referralUrl = `${window.location.origin}/signup?ref=${referralCode}`;
  const { t } = useTranslation();
  const [shareModalOpen, setShareModalOpen] = useState(false);

  return (
    <div className="w-full p-6">
      <div className="max-w-2xl">
        <div className="mb-6">
          <h2 className="text-xl font-medium text-grey-900 mb-2">{t('settings.inviteOrganizers')}</h2>
          <p className="text-sm text-grey-600">{t('settings.referralDesc')}</p>
        </div>

        <div className="bg-primary-25 border border-primary-100 p-4 rounded-lg mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-sm text-grey-700">
              <span className="font-medium">{t('common.note')}:</span> {t('settings.referralNoteText')}
            </div>
          </div>
        </div>
        
        <div className="bg-grey-50 p-6 rounded-lg mb-6">
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-1">
                {t('settings.referralCodeLabel')}
              </label>
              <div className="text-2xl font-mono font-medium text-grey-900">
                {referralCode}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-1">
                {t('settings.shareWithOrganizers')}
              </label>
              <button
                onClick={() => setShareModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share with Organizers
              </button>
              <ShareReferralModal
                isOpen={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                referralCode={referralCode}
                referralUrl={referralUrl}
              />
            </div>
          </div>
        </div>

        <div className="bg-grey-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-grey-900 mb-4">{t('settings.referralBenefits')}</h3>
          <ul className="list-disc list-inside space-y-2 text-sm text-grey-600">
            <li>{t('settings.referralBenefit1')}</li>
            <li>{t('settings.referralBenefit2')}</li>
            <li>{t('settings.referralBenefit3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ReferralContent; 